/**
 * usePeopleCounter Hook
 * 
 * Manages people counter state and logic for mesas in the Sales component.
 */

import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { updateMesaCurrentPeople } from '../../../../../../../../crud/management_api/mesas_info';

interface UsePeopleCounterProps {
  selectedMesa: any;
  isPedidosMode: boolean;
}

interface UsePeopleCounterReturn {
  currentPeople: number;
  setCurrentPeople: React.Dispatch<React.SetStateAction<number>>;
  updatingPeople: boolean;
  handleUpdatePeople: (newCount: number) => Promise<void>;
}

/**
 * Custom hook to manage people counter for a mesa
 */
export const usePeopleCounter = ({
  selectedMesa,
  isPedidosMode,
}: UsePeopleCounterProps): UsePeopleCounterReturn => {
  const [currentPeople, setCurrentPeople] = useState<number>(1);
  const [updatingPeople, setUpdatingPeople] = useState(false);

  // Keep currentPeople in sync with the mesa prop
  useEffect(() => {
    if (selectedMesa && !isPedidosMode) {
      const apiValue = selectedMesa.current_people ?? 1;
      setCurrentPeople(apiValue);
    } else if (isPedidosMode) {
      // For delivery/pickup, default to 1
      setCurrentPeople(1);
    }
  }, [selectedMesa, isPedidosMode]);

  const handleUpdatePeople = async (newCount: number) => {
    if (newCount < 1 || updatingPeople || !selectedMesa) return;
    
    const previousCount = currentPeople;
    
    // Optimistic update
    setCurrentPeople(newCount);
    
    setUpdatingPeople(true);
    try {
      await updateMesaCurrentPeople(selectedMesa.id, newCount);
    } catch (error) {
      // Rollback on error
      setCurrentPeople(previousCount);
      toast.error('Error actualizando cantidad de personas');
      console.error('Error updating people count:', error);
    } finally {
      setUpdatingPeople(false);
    }
  };

  return {
    currentPeople,
    setCurrentPeople,
    updatingPeople,
    handleUpdatePeople,
  };
};
